import type { MediaOptions, MusicRecommendationModeEnum } from 'js/src/client';
import { WorkspaceMediaModeEnum } from 'js/src/client';
import type { RootState } from 'js/src/libs/RootState';
import { createSelector } from 'reselect';

type MediaRecommendationPreferencesState = MediaOptions & {
    music_recommendation_mode: MusicRecommendationModeEnum;
};

export const selectMediaOptions = createSelector(
    (state: RootState) => state.mediaRecommendationPreferences,
    (preferences): MediaOptions => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { music_recommendation_mode, ...mediaOptions } =
            preferences as MediaRecommendationPreferencesState;
        return mediaOptions;
    },
);

export const selectSourceMediaEnabled = (state: RootState): boolean =>
    state.mediaRecommendationPreferences.source_media_enabled;

export const selectStockMediaEnabled = (state: RootState): boolean =>
    state.mediaRecommendationPreferences.stock_media_enabled;

export const selectStockIconsEnabled = (state: RootState): boolean =>
    state.mediaRecommendationPreferences.stock_icons_enabled;

export const selectDamMediaEnabled = (state: RootState): boolean =>
    state.mediaRecommendationPreferences.dam_media_enabled;

export const selectWorkspaceMediaMode = (state: RootState): WorkspaceMediaModeEnum =>
    state.mediaRecommendationPreferences.workspace_media_mode;

export const selectWorkspaceFolderIds = (state: RootState): number[] =>
    state.mediaRecommendationPreferences.workspace_folder_ids;

export const selectMediaGuidance = (state: RootState): string =>
    state.mediaRecommendationPreferences.media_guidance;

export const selectMinimumStockSuitabilityScore = (state: RootState): number | null =>
    state.mediaRecommendationPreferences.minimum_stock_suitability_score;

export const selectMusicRecommendationMode = (state: RootState): MusicRecommendationModeEnum =>
    (state.mediaRecommendationPreferences as MediaRecommendationPreferencesState)
        .music_recommendation_mode;

export const selectWillHaveNoMedia = (state: RootState): boolean => {
    // NOTE: ignoring source & icons here as those are very limited sources

    const prefs = state.mediaRecommendationPreferences;

    const workspaceMediaEnabled = prefs.workspace_media_mode !== WorkspaceMediaModeEnum.OFF;

    // Case 1: Stock, DAM and Workspace sources are disabled
    const allSourcesDisabled =
        !prefs.stock_media_enabled && !prefs.dam_media_enabled && !workspaceMediaEnabled;
    if (allSourcesDisabled) {
        return true;
    }

    // Case 2: Only workspace is enabled, mode is folders, but no folders selected
    const onlyWorkspaceEnabled =
        !prefs.stock_media_enabled && !prefs.dam_media_enabled && workspaceMediaEnabled;
    const foldersMode = prefs.workspace_media_mode === WorkspaceMediaModeEnum.FOLDERS;
    const noFoldersSelected = prefs.workspace_folder_ids.length === 0;

    if (onlyWorkspaceEnabled && foldersMode && noFoldersSelected) {
        return true;
    }

    return false;
};
